import React, { useMemo, memo } from 'react';
import { useAtomValue } from 'jotai';
import { Stack } from '@fiverr-private/layout_components';
import { computeHighlights } from '@fiverr-private/experts';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import { FederatedSeller, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { ROLLOUTS } from '../../../server/middlewares/addRolloutsData/constants';
import { useRolePageContext } from '../RolePageContext';
import { HighlightsData } from '../../types';
import { rolloutsAtom } from '../../atoms';
import { HighlightContent, HighlightTypes } from './HighlightContent';
import { BASE_HIGHLIGHTS_TO_SHOW, HIGHLIGHTS_TO_SHOW } from './constants';

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the Highlights Component');
    logger.error(err, {
        cause: originalError,
    });
};

export const Highlights = memo(() => {
    const sellerData = useSellerData(selectSellerSlice);
    const { highlightsData } = useRolePageContext();
    const { locale, browserLanguage, countryCode } = getContext();
    const rollouts = useAtomValue(rolloutsAtom);
    const inZebrasHourlyRatesRollout = rollouts[ROLLOUTS.ZEBRAS_HOURLY_RATES];

    const highlights = useMemo(() => {
        if (highlightsData) {
            const baseHighlights = computeHighlights({
                sellerData,
                collaborateData: (highlightsData as HighlightsData).collaborateData,
                matchingIndustriesData: (highlightsData as HighlightsData).matchingIndustriesData,
                locale,
                browserLanguage,
                countryCode,
                hideProVerifiedHighlight: true,
                highlightsToShow: BASE_HIGHLIGHTS_TO_SHOW,
            });

            if (baseHighlights.every((highlight) => highlight?.type !== HighlightTypes.RESPONSIVE)) {
                baseHighlights.push({
                    type: HighlightTypes.RESPONSE_TIME,
                });
            }

            if (inZebrasHourlyRatesRollout && sellerData.hasHourlyRates) {
                baseHighlights.push({
                    type: HighlightTypes.HOURLY_RATES,
                });
            }
            return baseHighlights;
        }
    }, [highlightsData, countryCode, sellerData, browserLanguage, locale, inZebrasHourlyRatesRollout]);

    if (highlights.length === 0) {
        return null;
    }

    return (
        <ErrorBoundary onError={onRenderError}>
            <Stack direction="column">
                {highlights.slice(0, HIGHLIGHTS_TO_SHOW).map(({ type }) => (
                    <HighlightContent key={type} type={type} />
                ))}
            </Stack>
        </ErrorBoundary>
    );
});

Highlights.displayName = 'Highlights';

function selectSellerSlice(seller: FederatedSeller) {
    return {
        username: seller.user.name,
        displayName: seller.user.profile.displayName,
        sellerId: seller.user.id,
        isHighlyResponsive: seller.isHighlyResponsive,
        isVerified: seller.isVerified,
        hasHourlyRates: !!seller.hourlyRate,
        languages: seller.user.languages,
        isPro: seller.isPro,
        isOnVacation: seller.isOnVacation,
    };
}
